/**
 * Immersive dark purple background for auth pages (login, forgot-password).
 *
 * Layers: base gradient, centered radial glow, floating SVG orbs,
 * concentric rotating rings, and a subtle grid texture.
 * Pure visual — renders no content.
 *
 * Mobile-safe: viewport-clamped sizes, reduced orb count on small screens,
 * and full prefers-reduced-motion support.
 *
 * Aliases: auth background, login background, auth page backdrop
 */

import { motion } from "motion/react";

/**
 * Orbs use percentage-based positions and viewport-relative radii via the
 * SVG viewBox, so they scale naturally. We keep 4 orbs (down from 6) to
 * reduce compositing layers on low-end mobile GPUs. The SVG viewBox is
 * 100x100 so all radii are in "vmin-ish" proportional units.
 */
const orbs = [
  { cx: 20, cy: 18, r: 12, opacity: 0.07, delay: 0, duration: 20 },
  { cx: 75, cy: 15, r: 8, opacity: 0.05, delay: 1.2, duration: 24 },
  { cx: 85, cy: 65, r: 14, opacity: 0.06, delay: 2.4, duration: 28 },
  { cx: 50, cy: 50, r: 18, opacity: 0.08, delay: 0, duration: 30 },
] as const;

/**
 * Ring diameters use CSS min() to cap at the viewport's smaller dimension,
 * preventing overflow on 375px-wide screens while still looking large on
 * desktop.
 */
const rings = [
  { vw: 45, maxPx: 320, opacity: 0.08, anim: "animate-spin-ring" },
  { vw: 70, maxPx: 500, opacity: 0.05, anim: "animate-spin-ring-slow" },
  { vw: 95, maxPx: 700, opacity: 0.03, anim: "animate-spin-ring-slower" },
] as const;

export function AuthBackground(): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 -z-10 overflow-hidden motion-reduce:![animation:none]"
    >
      {/* Base gradient — deep purple field */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0118] via-[#150830] to-[#0d0420]" />

      {/* Primary centered glow — clamped to 80vmin so it fits mobile viewports */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(168,85,247,0.18)_0%,transparent_65%)]"
        style={{
          width: "min(600px, 80vmin)",
          height: "min(600px, 80vmin)",
        }}
      />

      {/* Secondary off-center warm glow — clamped to 55vmin */}
      <div
        className="absolute top-[30%] left-[60%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.1)_0%,transparent_70%)]"
        style={{
          width: "min(400px, 55vmin)",
          height: "min(400px, 55vmin)",
        }}
      />

      {/*
        Floating orbs — SVG with a 100x100 viewBox so radii are proportional
        to the viewport. preserveAspectRatio="xMidYMid slice" ensures full
        coverage on any aspect ratio.
      */}
      <svg
        className="absolute inset-0 h-full w-full motion-reduce:hidden"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <radialGradient id="auth-orb" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#c084fc" stopOpacity="1" />
            <stop offset="100%" stopColor="#c084fc" stopOpacity="0" />
          </radialGradient>
        </defs>
        {orbs.map((orb, i) => (
          <motion.circle
            key={i}
            cx={orb.cx}
            cy={orb.cy}
            r={orb.r}
            fill="url(#auth-orb)"
            opacity={orb.opacity}
            initial={{ scale: 0.8 }}
            animate={{ scale: [0.8, 1.2, 0.8] }}
            transition={{
              duration: orb.duration,
              delay: orb.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{ willChange: "transform" }}
          />
        ))}
      </svg>

      {/* Concentric rotating rings — viewport-clamped diameters */}
      {rings.map((ring) => (
        <div
          key={ring.maxPx}
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-purple-400 motion-reduce:!animate-none ${ring.anim}`}
          style={{
            width: `min(${String(ring.maxPx)}px, ${String(ring.vw)}vmin)`,
            height: `min(${String(ring.maxPx)}px, ${String(ring.vw)}vmin)`,
            opacity: ring.opacity,
            willChange: "transform",
          }}
        />
      ))}

      {/* Grid texture — 32px on mobile, 40px on larger screens */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "min(40px, 8.5vw) min(40px, 8.5vw)",
        }}
      />

      {/* Vignette for depth — tighter on mobile to frame the card */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(5,0,15,0.7)_100%)] sm:bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(5,0,15,0.6)_100%)]" />
    </div>
  );
}
